import React from 'react';
import _ from 'underscore';
import Promise from 'bluebird';
import PropTypes from 'prop-types';
import { ImagePickerContainer } from '@bingads-webui-react/image-picker';
import { deserializeImageAssetMetadata } from '@bingads-webui-campaign/media-metadata-model';
import getUserImageItemWrapper from './user-image-wrapper';
import { UserPhotoDataSource } from '../data-source/user-photo-data-source';
import { ImageRepository } from '../../util/image-repository';
import LoadingPlaceholder from '../../components/loading-place-holder';
import { ImageExperienceImage } from '../../model/image-experience-image';
import { useImageDelete } from './image-delete-confirm';

const itemHeight = 180;

const UserImagePickerContainer = (props) => {
  const {
    i18n,
    userPhotoDataSource,
    staticImagePath,
    onImageSelected,
    imageRepository,
    showUiBlocker,
    showErrors,
    isLazyLoadEnabled,
    isForVNext,
    imageList,
    isAdCreatorEnabled,
    isAudienceAd,
    onKeyboardEscapeFromImage,
    isForThumbnail,
  } = props;

  const {
    ImageDeleteConfirm, onUserImageDelete,
  } = useImageDelete({
    i18n,
    showUiBlocker,
    imageRepository,
    userPhotoDataSource,
    imageList,
    isAudienceAd,
  });

  const onLoadMore = () => {
    userPhotoDataSource.loadMore();
  };

  const onUserImageEdit = (imageId, imageText) => {
    showUiBlocker(true);
    showErrors([]);

    return imageRepository.updateImageAsync({
      Id: imageId,
      Text: imageText,
    }).then(() => userPhotoDataSource.reload()).finally(() => {
      showUiBlocker(false);
    });
  };

  const onUserImageSelected = (selectedImages) => {
    // onImageSelected will also have ui blocker logic, so here we just turn on blocker earlier
    showUiBlocker(true);

    const image = selectedImages[0];
    let originalImageUrl;
    const extractedImageModel = _.extend({
      Id: image.id,
      IdString: image.idString,
      Height: image.height,
      Width: image.width,
      Source: image.source,
      Text: image.textFields[0],
      Url: image.src.replace('&h=180', ''),
    }, isAdCreatorEnabled && {
      BaseIdString: image.baseIdString,
      Metadata: image.metadata,
    });

    if (isAdCreatorEnabled
      && image.baseIdString && image.baseIdString !== image.id
      && deserializeImageAssetMetadata(image.metadata)) {
      const { data } = userPhotoDataSource.getData();
      const originalImage = _.find(data, item => item.id === image.baseIdString);

      if (originalImage) {
        originalImageUrl = originalImage.src.replace('&h=180', '');
        return Promise.resolve(onImageSelected({
          ...extractedImageModel,
          OriginalUrl: originalImageUrl,
        }));
      }

      // if base image doesn't fetch yet, need to explicitly fetch asset info
      return imageRepository.getImageByIdAsync(image.baseIdString)
        .then(({ Url: OriginalUrl }) => onImageSelected({
          ...extractedImageModel,
          OriginalUrl,
        }))
        .catch(() => {
          // callback onImageSelected already has blocker logic, so here only hide when error
          showUiBlocker(false);
        });
    }

    return Promise.resolve(onImageSelected(extractedImageModel));
  };

  const UserImageItemWrapper = getUserImageItemWrapper({
    i18n,
    isForVNext,
    onUserImageDelete,
    onUserImageEdit,
    isAdCreatorEnabled,
    isForThumbnail,
  });

  return (
    <div className="user-image-picker-container">
      <ImageDeleteConfirm />
      <ImagePickerContainer
        i18n={i18n}
        className="existing-images"
        dataSource={userPhotoDataSource}
        isMultiSelection={false}
        onLoadMore={onLoadMore}
        onSelectionChanged={onUserImageSelected}
        isAutoLoadMore
        isLazyLoadEnabled={isLazyLoadEnabled}
        maximizeFrame
        itemHeight={itemHeight}
        imageListItemWrapper={UserImageItemWrapper}
        imagePlaceHolder={LoadingPlaceholder}
        staticImagePath={staticImagePath}
        useBingAdsLoader
        onEscapeFromImage={onKeyboardEscapeFromImage}
      />
    </div>
  );
};

UserImagePickerContainer.propTypes = {
  userPhotoDataSource: PropTypes.instanceOf(UserPhotoDataSource).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  staticImagePath: PropTypes.string,
  showErrors: PropTypes.func,
  imageRepository: PropTypes.instanceOf(ImageRepository).isRequired,
  onImageSelected: PropTypes.func.isRequired,
  showUiBlocker: PropTypes.func,
  isForVNext: PropTypes.bool,
  isLazyLoadEnabled: PropTypes.bool.isRequired,
  imageList: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.instanceOf(ImageExperienceImage)),
    PropTypes.instanceOf(ImageExperienceImage),
    PropTypes.arrayOf(PropTypes.shape({ IdString: PropTypes.string })),
  ])),
  isAdCreatorEnabled: PropTypes.bool,
  isAudienceAd: PropTypes.bool,
  isForThumbnail: PropTypes.bool,
  onKeyboardEscapeFromImage: PropTypes.func,
};

UserImagePickerContainer.defaultProps = {
  showErrors: _.noop,
  showUiBlocker: _.noop,
  staticImagePath: '',
  isForVNext: false,
  imageList: [],
  isAdCreatorEnabled: false,
  isAudienceAd: false,
  isForThumbnail: false,
  onKeyboardEscapeFromImage: _.noop,
};

export default UserImagePickerContainer;
